import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect } from "react";
import { Icon } from "../Icon";
import { useLocalizedStrings } from "../LocalizationProvider";
import { Select } from "../Select";
import { cn } from "../utils/tailwind";
import { useFilterOperators } from "./common";
import { FilterValueInput, isValidDate, isValidNumber } from "./FilterValueInput";
export const FilterCondition = ({ condition, availableFields, onUpdate, onMajorUpdate, onMinorUpdate, onDelete, showLogic = false, logicKind = 'And', onLogicChange, isLogicEditable = false, }) => {
    if (!availableFields)
        return null;
    const L = useLocalizedStrings();
    const { LOGIC_OPTIONS, OPERATORS_BY_TYPE } = useFilterOperators();
    const [localCondition, setLocalCondition] = React.useState(null);
    const isDateConditionComplete = (field, operator, value) => {
        if (!field || field.type !== 'date')
            return true;
        const operatorConfig = OPERATORS_BY_TYPE[field.type]?.find(op => op.value === operator);
        if (!operatorConfig?.requiresValue)
            return true;
        return value !== undefined && value !== '' && isValidDate(value);
    };
    const isNumberConditionComplete = (field, operator, value) => {
        if (!field || field.type !== 'number')
            return true;
        const operatorConfig = OPERATORS_BY_TYPE[field.type]?.find(op => op.value === operator);
        if (!operatorConfig?.requiresValue)
            return true;
        return value !== undefined && value !== '' && isValidNumber(value);
    };
    const isConditionComplete = (field, operator, value) => {
        return isDateConditionComplete(field, operator, value) && isNumberConditionComplete(field, operator, value);
    };
    const displayCondition = localCondition || condition;
    const selectedField = availableFields.find(f => f.key === displayCondition.field);
    const availableOperators = selectedField ? OPERATORS_BY_TYPE[selectedField.type] : [];
    const availableFieldOptions = availableFields.map((f) => ({
        value: f.key,
        label: f.label
    }));
    const selectedFieldOption = availableFieldOptions.find(f => f.value === selectedField?.key);
    const selectedOperator = availableOperators.find(op => op.value === displayCondition.operator);
    const requiresValue = selectedOperator?.requiresValue ?? true;
    const operatorOptions = availableOperators.map(op => ({
        value: op.value,
        label: op.label
    }));
    const handleUpdate = (updates) => {
        const updatedCondition = {
            ...displayCondition,
            ...updates,
        };
        const isComplete = isConditionComplete(availableFields.find(f => f.key === updatedCondition.field), updatedCondition.operator, updatedCondition.value);
        if (!isComplete) {
            setLocalCondition(updatedCondition);
        }
        else {
            setLocalCondition(null);
            onUpdate(updatedCondition);
        }
    };
    const handleMajorUpdate = (updates) => {
        const updatedCondition = {
            ...displayCondition,
            ...updates,
        };
        const isComplete = isConditionComplete(availableFields.find(f => f.key === updatedCondition.field), updatedCondition.operator, updatedCondition.value);
        if (!isComplete) {
            setLocalCondition(updatedCondition);
        }
        else {
            setLocalCondition(null);
            onMajorUpdate?.(updatedCondition) || onUpdate(updatedCondition);
        }
    };
    const handleMinorUpdate = (updates) => {
        const updatedCondition = {
            ...displayCondition,
            ...updates,
        };
        const isComplete = isConditionComplete(availableFields.find(f => f.key === updatedCondition.field), updatedCondition.operator, updatedCondition.value);
        if (!isComplete) {
            setLocalCondition(updatedCondition);
        }
        else {
            setLocalCondition(null);
            onMinorUpdate?.(updatedCondition) || onUpdate(updatedCondition);
        }
    };
    const handleValidDateUpdate = (updates) => {
        const updatedCondition = {
            ...displayCondition,
            ...updates,
        };
        setLocalCondition(null);
        onMajorUpdate?.(updatedCondition) || onUpdate(updatedCondition);
    };
    const handleValidNumberUpdate = (updates) => {
        const updatedCondition = {
            ...displayCondition,
            ...updates,
        };
        setLocalCondition(null);
        onMajorUpdate?.(updatedCondition) || onUpdate(updatedCondition);
    };
    useEffect(() => {
        if (!displayCondition.field && availableFields.length > 0) {
            const defaultField = availableFields[0];
            const defaultOperators = OPERATORS_BY_TYPE[defaultField.type] || [];
            const defaultOperator = defaultOperators[0]?.value || 'equals';
            const defaultValue = defaultOperators[0]?.requiresValue ? displayCondition.value : undefined;
            const updates = {
                field: defaultField.key,
                operator: defaultOperator,
                value: defaultValue
            };
            const isComplete = isConditionComplete(defaultField, defaultOperator, defaultValue);
            if (!isComplete) {
                setLocalCondition({
                    ...displayCondition,
                    ...updates
                });
            }
            else {
                onUpdate(updates);
            }
        }
    }, [displayCondition.field, availableFields, onUpdate]);
    return (_jsxs("div", { className: "flex flex-col sm:flex-row gap-2", "data-condition-id": condition.id, children: [showLogic && (isLogicEditable ? (_jsx(Select, { className: "w-[74px]", value: LOGIC_OPTIONS.find((x) => x.value === logicKind), onChange: (value) => onLogicChange?.(value.value), options: LOGIC_OPTIONS, description: '', label: L.component_library.filter_panel.overall_condition_operator, labelLocation: 'above', hideLabel: true, portal: true })) : (_jsx("span", { className: cn("w-[74px]", "text-left text-sm text-gray-800 whitespace-nowrap mt-2 sm:text-center"), children: logicKind === 'And' ? L.component_library.filter_panel.and_lower : L.component_library.filter_panel.or_lower }))), _jsx("span", { className: cn("text-left text-sm text-gray-800 whitespace-nowrap mt-2", !showLogic && "w-[121px]"), children: showLogic ? L.component_library.filter_panel.where_lower : L.component_library.filter_panel.where_upper }), _jsxs("div", { className: "flex flex-col sm:flex-row gap-1 flex-1 min-w-0 items-start", children: [_jsx(Select, { anchor: "bottom start", virtualized: true, className: 'flex-1 sm:min-w-32 lg:min-w-56', label: L.component_library.filter_panel.select_a_field, description: L.component_library.filter_panel.select_a_field, options: availableFieldOptions, value: selectedFieldOption || undefined, onChange: (field) => {
                            if (!field)
                                return;
                            const newField = availableFields.find(f => f.key === field.value);
                            const newOperators = newField ? OPERATORS_BY_TYPE[newField.type] : [];
                            const defaultOperator = newOperators[0]?.value || 'equals';
                            let newValue;
                            if (!newOperators[0]?.requiresValue) {
                                newValue = undefined;
                            }
                            else if (newField?.type === 'date') {
                                newValue = "";
                            }
                            else if (newField?.type === 'number') {
                                newValue = "";
                            }
                            else {
                                newValue = condition.value ?? "";
                            }
                            const updates = {
                                field: field.value,
                                operator: defaultOperator,
                                value: newValue
                            };
                            handleMajorUpdate(updates);
                        }, hideLabel: true, portal: true, searchable: true, filterFunction: (option, query) => {
                            const queryLower = query.trim().toLowerCase();
                            const labelMatch = String(option.label).trim().toLowerCase().includes(queryLower);
                            const keyMatch = option.value.toString().toLowerCase().includes(queryLower);
                            return labelMatch || keyMatch;
                        } }), _jsx(Select, { searchable: true, anchor: "bottom start", className: "sm:min-w-8 lg:min-w-28", options: operatorOptions, value: selectedOperator, onChange: (operator) => {
                            if (!operator)
                                return;
                            const newOperator = availableOperators.find(op => op.value === operator.value);
                            let newValue;
                            if (!newOperator?.requiresValue) {
                                newValue = undefined;
                            }
                            else if (selectedField?.type === 'number') {
                                newValue = (condition.value && isValidNumber(condition.value)) ? condition.value : "";
                            }
                            else {
                                newValue = condition.value ?? "";
                            }
                            handleMajorUpdate({
                                operator: operator.value,
                                value: newValue
                            });
                        }, description: '', label: L.component_library.filter_panel.filter_condition_operator, labelLocation: 'above', hideLabel: true, portal: true }), requiresValue && (_jsx("div", { className: `sm:min-w-32 md:min-w-36 ${selectedField?.type === 'text' ? 'lg:min-w-64' : ''}`, children: _jsx(FilterValueInput, { fieldType: selectedField?.type || 'text', value: displayCondition.value || '', onChange: (value) => handleUpdate({ value }), onDebouncedChange: (value) => handleMinorUpdate({ value }), onValidChange: selectedField?.type === 'date' ?
                                (value) => handleValidDateUpdate({ value }) :
                                selectedField?.type === 'number' ?
                                    (value) => handleValidNumberUpdate({ value }) : undefined, placeholder: L.component_library.filter_panel.enter_a_value }) })), onDelete && (_jsx("button", { type: "button", "aria-label": L.component_library.filter_panel.delete, onClick: onDelete, className: "h-9 px-2 sm:ml-1 bg-[#FFFFFF] border border-gray-300 hover:border-gray-400 hover:bg-gray-100 rounded flex items-center justify-center transition-colors duration-300", children: _jsx(Icon, { name: "TrashIcon", type: "solid", size: "sm" }) }))] })] }));
};
